'use client'

import React from 'react'
import { ThemeProvider as NextThemesProvider } from 'next-themes'
import { ThemeProviderProps } from 'next-themes/dist/types'
import { TooltipProvider } from '@/components/ui/tooltip'
import { SessionProvider } from 'next-auth/react'
import RefreshSdkToken from '@/components/refresh-sdk-token'
import { AdminSettings } from '@/lib/admin'
import NiceModal from '@ebay/nice-modal-react';
import dynamic from 'next/dynamic'
import { PromptTemplatesProvider } from '@/lib/hooks/prompt-templates/use-prompt-templates'
import { useAdminStore } from '@/lib/store/use-admin-store'
import { Session } from 'next-auth'
import { initClientStores } from '@/lib/store/init-client-stores'

// Import i18n directly to ensure it's initialized before any components use it
import '@/lib/i18n'

interface ProvidersProps extends ThemeProviderProps {
  isAdminUser?: boolean
  adminSettings?: AdminSettings
  session?: Session | null
}

const AmplitudeContextProvider = dynamic(
  () => import('@/components/amplitude-context'),
  { ssr: false }
)

// Utility to check if we're running on the client
const isClient = typeof window !== 'undefined';

function useInitClientStores() {
  const hasInitialized = React.useRef(false);

  React.useEffect(() => {
    if (hasInitialized.current || !isClient) return;

    const doInit = async () => {
      try {
        await initClientStores();
        hasInitialized.current = true;
      } catch (error) {
        console.error('Failed to initialize stores:', error);
      }
    };

    doInit();

  }, []);
}

export function Providers({ children, isAdminUser, adminSettings, session, ...props }: ProvidersProps) {
  useInitClientStores();
  
  const setIsAdminUser = useAdminStore((store) => store.setIsAdminUser)
  const setAdminSettings = useAdminStore((store) => store.setAdminSettings)

  React.useEffect(() => {
    setIsAdminUser(isAdminUser);
  }, [isAdminUser, setIsAdminUser]);

  React.useEffect(() => {
    setAdminSettings(adminSettings);
  }, [adminSettings, setAdminSettings]);

  return (
    <NextThemesProvider {...props}>
      <SessionProvider session={session}>
        <TooltipProvider>
          <PromptTemplatesProvider>
            <NiceModal.Provider>
              <AmplitudeContextProvider>
                  {children}
              </AmplitudeContextProvider>
            </NiceModal.Provider>
          </PromptTemplatesProvider>
          <RefreshSdkToken />
        </TooltipProvider>
      </SessionProvider>
    </NextThemesProvider>
  )
}
